import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    Heading,
    Layer,
    Meter,
    Page,
    PageContent,
    Tag,
    Text,
    TextArea,
} from 'grommet';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { API, AppConstants, Utils } from '@enrich/shared-lib';
import QueryString from 'qs';

export const Dashboard = () => {
    const user = useOutletContext();
    const school = user.school;
    const { schoolSlug } = useParams();
    const navigate = useNavigate();
    const [activeCount, setActiveCount] = useState(0);
    const [alumniCount, setAlumniCount] = useState(0);
    const [nonActiveCount, setNonActiveCount] = useState(0);
    const [recentEvents, setRecentEvents] = useState([]);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const [active, alumni, nonActive, events] = await Promise.all([
                loadActiveCount(school.id),
                loadAlumniCount(school.id),
                loadNonActiveCount(school.id),
                loadRecentEvents(school.id),
            ]);

            if (isActive) {
                setActiveCount(active);
                setAlumniCount(alumni);
                setNonActiveCount(nonActive);
                setRecentEvents(events);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [school.id]);

    const total = activeCount + alumniCount + nonActiveCount;

    const emailTemplate = `Hi [Student Name],

We're excited to share Enrich, a career program designed to support historically underrepresented students by connecting them to employers, preparation resources, and real career opportunities.

Upperclassmen can be matched directly to open internships and full-time roles with hiring employers.

Underclassmen can be matched to role-specific pipelines for particular types of internships or entry-level roles. These pipelines focus on building relevant skills, facilitating employer engagement, and leading into future job opportunities.

Enrich offers:
- Access to internships and full-time roles across industries
- Role-specific pathways aligned to real hiring needs
- Direct employer engagement beyond traditional job boards
- Structured interview preparation and career support

If you're exploring internships or full-time roles now, or want to prepare for specific roles you are interested in pursuing, Enrich may be a valuable resource.

Apply here: https://www.enrichscholars.com/apply-today/
Learn more: https://www.enrichscholars.com/

Best,
[Career Services]`;

    const handleDownloadFlyer = async () => {
        const url = process.env.REACT_APP_APPLICATION_FLYER_URL;
        if (!url) {
            console.log('REACT_APP_APPLICATION_FLYER_URL not configured');
            return;
        }
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Flyer fetch failed: ${response.status}`);
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = 'Enrich-Application-Flyer.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.log(error);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(emailTemplate).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box direction="row" justify="between" align="center">
                    <Heading level="3">{school.name} Dashboard</Heading>
                    <Box direction="row" gap="small">
                        <Button
                            label="Copy Email Template"
                            onClick={() => setShowEmailModal(true)}
                            secondary
                        />
                        <Button
                            label="Share Application Flyer"
                            onClick={handleDownloadFlyer}
                            secondary
                        />
                    </Box>
                </Box>
                {showEmailModal && (
                    <Layer
                        onEsc={() => setShowEmailModal(false)}
                        onClickOutside={() => setShowEmailModal(false)}
                        position="center">
                        <Box pad="large" gap="medium" width="large">
                            <Box
                                direction="row"
                                justify="between"
                                align="center">
                                <Heading level="4" margin="none">
                                    Email Template
                                </Heading>
                                <Button
                                    label="Close"
                                    onClick={() => setShowEmailModal(false)}
                                    plain
                                />
                            </Box>
                            <Text size="small" color="dark-4">
                                Copy this template and paste it into your email
                                client. Replace the bracketed placeholders with
                                the appropriate information.
                            </Text>
                            <TextArea
                                value={emailTemplate}
                                readOnly
                                rows={18}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                }}
                            />
                            <Box align="end">
                                <Button
                                    primary
                                    label={
                                        copied ? 'Copied!' : 'Copy to Clipboard'
                                    }
                                    onClick={handleCopyEmail}
                                />
                            </Box>
                        </Box>
                    </Layer>
                )}
                <Box direction="row" gap="medium" wrap>
                    <StatCard
                        label="Active Students"
                        value={activeCount}
                        onClick={() =>
                            navigate(`/${schoolSlug}/active-students`)
                        }
                    />
                    <StatCard
                        label="Non-Active Students"
                        value={nonActiveCount}
                        onClick={() => navigate(`/${schoolSlug}/non-active`)}
                    />
                    <StatCard
                        label="Alumni"
                        value={alumniCount}
                        onClick={() => navigate(`/${schoolSlug}/alumni`)}
                    />
                    <StatCard
                        label="Total Students"
                        value={total}
                        onClick={() => navigate(`/${schoolSlug}/all-students`)}
                    />
                </Box>
                {total > 0 && (
                    <Box
                        gap="small"
                        pad="medium"
                        elevation="small"
                        round="small">
                        <Text weight="bold">Programme Status</Text>
                        <Meter
                            values={[
                                {
                                    value: activeCount,
                                    label: 'Active',
                                    color: 'status-ok',
                                },
                                {
                                    value: alumniCount,
                                    label: 'Alumni',
                                    color: 'accent-1',
                                },
                                {
                                    value: nonActiveCount,
                                    label: 'Non-Active',
                                    color: 'status-warning',
                                },
                            ]}
                            max={total}
                            type="bar"
                            size="full"
                        />
                        <Box direction="row" justify="between">
                            <Text size="small">{activeCount} active</Text>
                            <Text size="small">{alumniCount} alumni</Text>
                            <Text size="small">
                                {nonActiveCount} non-active
                            </Text>
                        </Box>
                    </Box>
                )}
                {recentEvents.length > 0 && (
                    <Box gap="small">
                        <Heading level="4" margin="none">
                            Recent Activity
                        </Heading>
                        <Box
                            pad="medium"
                            elevation="small"
                            round="small"
                            gap="small"
                            height={{ max: 'large' }}
                            overflow={{ vertical: 'auto' }}>
                            {recentEvents.map((event) => (
                                <Box
                                    key={event.id}
                                    direction="row"
                                    gap="medium"
                                    align="center">
                                    <Tag
                                        value={new Date(
                                            event.createdAt,
                                        ).toLocaleDateString()}
                                        size="xsmall"
                                    />
                                    <Text size="small">
                                        <Anchor
                                            href={`/${schoolSlug}/students/${event.contact.id}`}
                                            label={`${event.contact.firstName} ${event.contact.lastName}`}
                                        />{' '}
                                        {formatEventType(event.type)}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </PageContent>
        </Page>
    );
};

const StatCard = ({ label, value, onClick }) => (
    <Box
        pad="medium"
        elevation="small"
        round="small"
        align="center"
        gap="small"
        width="small"
        onClick={onClick}
        focusIndicator={false}
        style={{ cursor: 'pointer' }}>
        <Text size="xxlarge" weight="bold">
            {value}
        </Text>
        <Text size="small">{label}</Text>
    </Box>
);

const loadActiveCount = async (schoolId) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'active',
            school: schoolId,
        },
        pagination: { pageSize: 1 },
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadRecentEvents = async (schoolId) => {
    const gqlQuery = `
        query getRecentEvents($schoolId: ID!) {
            contacts(
                filters: { school: { id: { eq: $schoolId } } }
                pagination: { pageSize: 100 }
            ) {
                data {
                    id
                    attributes {
                        firstName
                        lastName
                        events(
                            filters: { type: { nei: "portal_login" } }
                            sort: ["createdAt:desc"]
                            pagination: { pageSize: 5 }
                        ) {
                            data {
                                id
                                attributes {
                                    type
                                    createdAt
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    try {
        const response = await API.post('graphql', {
            query: gqlQuery,
            variables: { schoolId },
        });
        const contacts = response.data.data?.contacts?.data ?? [];
        const events = contacts.flatMap((c) =>
            (c.attributes.events?.data ?? []).map((e) => ({
                id: e.id,
                type: e.attributes.type,
                createdAt: e.attributes.createdAt,
                contact: {
                    id: c.id,
                    firstName: c.attributes.firstName,
                    lastName: c.attributes.lastName,
                },
            })),
        );
        return events
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 20);
    } catch (error) {
        console.log(error);
        return [];
    }
};

const formatEventType = (type) =>
    type
        ?.split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ') ?? type;

const loadAlumniCount = async (schoolId) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'alumni',
            school: schoolId,
        },
        pagination: { pageSize: 1 },
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadNonActiveCount = async (schoolId) => {
    const query = QueryString.stringify({
        populate: {
            contacts: {
                filters: {
                    enrichProgramStatus: {
                        $in: AppConstants.nonActiveStudentStatuses,
                    },
                },
                fields: ['id'],
            },
        },
    });

    try {
        const response = await API.get(`/api/schools/${schoolId}?${query}`);
        const schoolData = response.data.data;
        const flattened = Utils.deepFlatten(schoolData);
        return flattened.contacts?.length ?? 0;
    } catch (error) {
        console.log(error);
        return 0;
    }
};
